import apiClient, { handleUnauthorizedResponse } from "./apiClient"

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:4000"

async function downloadFile(path: string, fallbackName: string): Promise<void> {
    const token = localStorage.getItem("accessToken")
    const response = await fetch(`${API_BASE_URL}${path}`, {
        headers: { Authorization: `Bearer ${token || ""}` },
    })
    if (!response.ok) {
        const err = await response.json().catch(() => null)
        handleUnauthorizedResponse(response.status, err?.error?.message)
        throw new Error(
            err?.error?.message || `Export failed (${response.status})`
        )
    }

    // Extract filename from Content-Disposition header if available
    const disposition = response.headers.get("Content-Disposition") || ""
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/)
    const filename = filenameMatch ? filenameMatch[1] : fallbackName

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
}

interface ReportFilters {
    outletId?: string
    registerId?: string
    dateFrom?: string
    dateTo?: string
    format?: "json" | "csv" | "pdf"
}

class ReportsService {
    async getZReport(companyId: string, cashSessionId: string) {
        const response = await apiClient.get(
            `/api/companies/${companyId}/reports/z-report?cashSessionId=${cashSessionId}`
        )
        return response.report
    }

    async getDailyExternalZReport(
        companyId: string,
        date: string,
        outletId?: string
    ) {
        const params = new URLSearchParams({ date })
        if (outletId) params.set("outletId", outletId)
        const response = await apiClient.get(
            `/api/companies/${companyId}/reports/z-report/daily-external?${params.toString()}`
        )
        return response.report
    }

    async getSalesJournal(companyId: string, filters: ReportFilters = {}) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/sales-journal?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(path, `sales_journal_${Date.now()}.csv`)
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(path, `sales_journal_${Date.now()}.pdf`)
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async getCollectionsReport(companyId: string, filters: ReportFilters = {}) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/collections?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(path, `collections_${Date.now()}.csv`)
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(path, `collections_${Date.now()}.pdf`)
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async getVATBreakdown(companyId: string, filters: ReportFilters = {}) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/vat-breakdown?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(path, `vat_breakdown_${Date.now()}.csv`)
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(path, `vat_breakdown_${Date.now()}.pdf`)
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async getRevenueByCategory(companyId: string, filters: ReportFilters = {}) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/revenue-by-category?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(path, `revenue_by_category_${Date.now()}.csv`)
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(path, `revenue_by_category_${Date.now()}.pdf`)
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async getRevenueByPaymentMethod(
        companyId: string,
        filters: ReportFilters = {}
    ) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/revenue-by-payment-method?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(
                path,
                `revenue_by_payment_method_${Date.now()}.csv`
            )
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(
                path,
                `revenue_by_payment_method_${Date.now()}.pdf`
            )
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async getFactureXReport(companyId: string, filters: ReportFilters = {}) {
        const params = new URLSearchParams()

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, value.toString())
            }
        })

        const path = `/api/companies/${companyId}/reports/facture-x?${params.toString()}`

        if (filters.format === "csv") {
            await downloadFile(path, `facture_x_${Date.now()}.csv`)
            return null
        }
        if (filters.format === "pdf") {
            await downloadFile(path, `facture_x_${Date.now()}.pdf`)
            return null
        }

        const response = await apiClient.get(path)
        return response
    }

    async downloadReport(
        companyId: string,
        reportType: string,
        filters: ReportFilters = {},
        format: "csv" | "pdf" = "csv"
    ) {
        const params = new URLSearchParams({
            ...filters,
            format,
        } as any)

        const path = `/api/companies/${companyId}/reports/${reportType}?${params.toString()}`
        const ext = format === "csv" ? "csv" : "pdf"
        await downloadFile(path, `${reportType}_${Date.now()}.${ext}`)
    }
}

const reportsService = new ReportsService()

export default reportsService
